import { FILE_SIZE_LIMITS } from "@mg/schemas/src/commons/file-limits";
import { z } from "zod";

import {
  uploadFileWithMultipart,
  supportsMultipartUpload,
} from "./multipartUpload";

import { UPLOAD_API_URL } from "../config/env";
import { posthogClient } from "../config/posthog";
import { sanitizeFilename } from "../utils/files";
import { smartFetch } from "../utils/http";
import { resizeImageToSquare } from "../utils/imageHandler";

// Use centralized file size limits
export const MAX_FILE_SIZE_MB = FILE_SIZE_LIMITS.UPLOAD.MAX_SIZE_MB;
export const MAX_IMAGE_DIMENSION = FILE_SIZE_LIMITS.IMAGE.MAX_DIMENSION;

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
] as const;

export const presignedPostParser = z.object({
  url: z.string().url(),
  fields: z.object({
    key: z.string(),
    bucket: z.string(),
    "X-Amz-Algorithm": z.string(),
    "X-Amz-Credential": z.string(),
    "X-Amz-Date": z.string(),
    Policy: z.string(),
    "X-Amz-Signature": z.string(),
  }),
});

export type PresignedPost = z.infer<typeof presignedPostParser>;

export async function presignedPost(
  filename: string,
  useAcceleration = false,
  fileType = "image/png",
  isRevisionFile = false,
) {
  const now = performance.now();

  posthogClient.capture("presigned_url_start", {
    filename,
    useAcceleration,
    initiatedAt: now,
  });

  try {
    const json = await smartFetch(UPLOAD_API_URL, {
      method: "POST",
      body: JSON.stringify({
        filename,
        useAcceleration,
        fileType,
        isRevisionFile,
      }),
    });

    const parsedData = presignedPostParser.parse(json);
    const newNow = performance.now();

    posthogClient.capture("presigned_url_complete", {
      response: parsedData,
      elapsed: newNow - now,
    });

    return parsedData;
  } catch (e: unknown) {
    const newNow = performance.now();

    posthogClient.capture("presigned_url_error", {
      error: (e as Error).message,
      stack: (e as Error).stack,
      elapsed: newNow - now,
    });
  }
}

type UploadToS3Payload = {
  url: string;
  payload: FormData;
  abortSignal?: AbortSignal;
  maxRetries?: number;
};

export async function uploadToS3(payload: UploadToS3Payload) {
  const maxRetries = payload.maxRetries ?? 2;
  // payload should already be validated by this point
  const urlParser = z.string().url();
  urlParser.parse(payload.url);
  const startTime = performance.now();

  const connection = (navigator as any).connection;
  const file = payload.payload.get("file") as File;

  let lastError: Error | undefined;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const now = performance.now();

    if (attempt > 0) {
      // Exponential backoff: 1s, 2s
      const delay = Math.pow(2, attempt) * 1000;
      console.log(
        `Retrying S3 upload (attempt ${attempt + 1}/${maxRetries + 1}) after ${delay}ms...`,
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    posthogClient.capture("s3_upload_start", {
      url: payload.url,
      payload: Object.fromEntries(
        Array.from(payload.payload.entries()).filter(([key]) => key !== "file"),
      ),
      initiatedAt: now,
      attempt: attempt + 1,
      key: payload.payload.get("key"),
      bucket: payload.payload.get("bucket"),
      fileName: file?.name,
      fileSizeMB: (file?.size || 0) / (1024 * 1024),
      fileType: file?.type,
      lastModified: file?.lastModified,
      fileExtension: file?.name.split(".").pop()?.toLowerCase(),
      // Add properties to report internet upload speed
      connectionType: connection?.type,
      effectiveType: connection?.effectiveType,
      downlink: connection?.downlink,
      rtt: connection?.rtt,
      saveData: connection?.saveData,
    });

    try {
      const response = await fetch(payload.url, {
        method: "POST",
        body: payload.payload,
        signal: payload.abortSignal,
      });

      let responseText: string | undefined;
      try {
        responseText = await response.text();
      } catch {
        // Could not read response; do nothing
      }

      const analyticsPayload = {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url,
        responseText,
      };

      if (!response.ok) {
        const newNow = performance.now();
        posthogClient.capture("s3_upload_error", {
          ...analyticsPayload,
          elapsed: newNow - now,
          attempt: attempt + 1,
        });

        // Parse XML error response from S3 if available
        let errorMessage = `Upload failed with status ${response.status}`;
        if (responseText) {
          try {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(responseText, "text/xml");
            const errorCode = xmlDoc.querySelector("Code")?.textContent;
            const errorMsg = xmlDoc.querySelector("Message")?.textContent;
            if (errorCode || errorMsg) {
              errorMessage = `S3 Error: ${errorCode || "Unknown"} - ${errorMsg || "No details"}`;
            }
          } catch {
            // If XML parsing fails, include raw response text
            errorMessage += ` - Response: ${responseText.substring(0, 200)}`;
          }
        }

        lastError = new Error(errorMessage);

        // Retry on 5xx errors or network issues, but not on 4xx client errors
        if (response.status >= 500 || response.status === 429) {
          if (attempt < maxRetries) {
            console.warn(
              `S3 upload failed with ${response.status}, will retry...`,
            );
            continue;
          }
        }

        throw lastError;
      }

      const newNow = performance.now();

      posthogClient.capture("s3_upload_complete", {
        ...analyticsPayload,
        elapsed: newNow - now,
        totalElapsed: newNow - startTime,
        attempt: attempt + 1,
      });

      return undefined;
    } catch (error: unknown) {
      if (error instanceof Error && error.name === "AbortError") {
        posthogClient.capture("s3_upload_aborted", {
          fileName: file?.name,
          elapsed: performance.now() - startTime,
        });
        throw new Error("Upload was cancelled");
      }

      lastError = error instanceof Error ? error : new Error(String(error));

      // Don't retry if it's a client error (4xx) or user cancelled
      if (
        lastError.message.includes("cancelled") ||
        lastError.message.includes("400") ||
        lastError.message.includes("403") ||
        lastError.message.includes("404")
      ) {
        throw lastError;
      }

      // Retry on network errors
      if (attempt < maxRetries) {
        console.warn(
          `S3 upload attempt ${attempt + 1} failed, will retry...`,
          lastError,
        );
        continue;
      }

      throw lastError;
    }
  }

  throw lastError || new Error("Upload failed after all retries");
}

export async function updateAvatar(file: File) {
  if (file == null) {
    console.warn("Attempting to update user avatar, but no file was provided");
    return;
  }
  const cleanName = file.name.replace(/[!'()*]/g, "");

  // Use multipart upload if supported (more reliable than direct upload)
  if (supportsMultipartUpload()) {
    const { key, location } = await uploadFileWithMultipart(file, {
      useAcceleration: false,
    });
    // Return compatible format with fields.key for existing consumers
    return { fields: { key }, url: location };
  }

  // Fall back to direct upload only if multipart upload is not supported
  const response = await presignedPost(cleanName, false, "image/jpeg", false);
  const { fields, url } = response as PresignedPost;
  const payload = new FormData();
  Object.keys(fields).forEach((key) =>
    payload.append(
      key,
      fields[key as keyof PresignedPost["fields"]] as string | Blob,
    ),
  );
  payload.set("file", file, cleanName);
  await fetch(url, { body: payload, method: "POST" });
  return { fields, url };
}

export async function uploadImageAsset(
  file: File,
  downscaleLargeImages = false,
) {
  if (file == null) {
    console.warn("Attempting to upload image asset, but no file was provided");
    return;
  }

  // Handle downscaling for large images
  if (
    downscaleLargeImages &&
    file.size > FILE_SIZE_LIMITS.IMAGE.MAX_SIZE_BYTES
  ) {
    if (file.type.includes("image")) {
      const startTime = performance.now();
      const initialSize = file.size;

      file = await scaleImage(file);

      posthogClient.capture("image_scaled_down", {
        filename: file.name,
        initialSize,
        finalSize: file.size,
        durationMS: performance.now() - startTime,
      });
      console.warn(
        `Downscaling ${file.name} to reduce the file size from ${Math.round(file.size / 1024 / 1024)}MB.`,
      );
    } else {
      console.warn(
        `File ${file.name} is large (${Math.round(file.size / 1024 / 1024)}MB).`,
      );
    }
  }

  const cleanName = sanitizeFilename(file.name);

  // Always use multipart upload if supported (more reliable than direct upload)
  if (supportsMultipartUpload()) {
    const { key, location } = await uploadFileWithMultipart(file, {
      useAcceleration: true,
      onProgress: (progress) => {
        posthogClient.capture("multipart_upload_progress", {
          filename: cleanName,
          totalParts: progress.totalParts,
          completedParts: progress.completedParts,
          totalBytes: progress.totalBytes,
          uploadedBytes: progress.uploadedBytes,
        });
      },
    });

    // Create a FormData object with the key and location for compatibility with existing code
    const payload = new FormData();
    payload.append("key", key);
    payload.append("location", location);
    return payload;
  }

  // Fall back to direct upload only if multipart upload is not supported
  const data = await presignedPost(cleanName, true, file.type, true).catch(
    () => {
      throw new Error("An error occurred while uploading the file");
    },
  );
  if (!data) {
    throw new Error("An error occurred while uploading the file");
  }
  const payload = new FormData();

  payload.append("url", data.url);

  Object.keys(data.fields).forEach((key) =>
    payload.append(key, data.fields[key as keyof typeof data.fields]),
  );

  // set `file` last because AWS stops reading keys after it.
  payload.set("file", file, cleanName);

  return payload;
}

export const screenshotURLFromPayload = (payload: FormData) => {
  const screenshotUrl = `https://static.puntt.ai/${payload?.get("key")}`;
  return screenshotUrl;
};

// Organize logic used to modify the payload and pull out the URL
export const urlAndPayloadFromPayload = (payload: FormData) => {
  const url = payload?.get("url") as string;
  payload.delete("url");
  return { url, payload };
};

function scaleImage(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      const canvas = document.createElement("canvas");
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions if the image is too large
      if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
        if (width > height) {
          height = Math.floor((height / width) * MAX_IMAGE_DIMENSION);
          width = MAX_IMAGE_DIMENSION;
        } else {
          width = Math.floor((width / height) * MAX_IMAGE_DIMENSION);
          height = MAX_IMAGE_DIMENSION;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (blob) {
            resolve(new File([blob], file.name, { type: file.type }));
          } else {
            reject(new Error("Canvas toBlob failed"));
          }
        }, file.type);
      } else {
        URL.revokeObjectURL(url); // Release the object URL on failure
        reject(new Error("Failed to get canvas context"));
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Image load error"));
    };

    img.src = url;
  });
}

export async function uploadAvatar(file: File) {
  if (!file) {
    throw new Error("No file provided for avatar upload");
  }

  if (
    !ALLOWED_IMAGE_TYPES.includes(
      file.type as (typeof ALLOWED_IMAGE_TYPES)[number],
    )
  ) {
    throw new Error("Only JPG, PNG, GIF and WebP images are allowed");
  }

  // Resize to 200x200 for retina support
  const resizedFile = await resizeImageToSquare(file, 200);

  // Use multipart upload if supported (more reliable than direct upload)
  if (supportsMultipartUpload()) {
    const { key } = await uploadFileWithMultipart(resizedFile, {
      useAcceleration: false,
    });
    return key;
  }

  // Fall back to direct upload only if multipart upload is not supported
  const response = await presignedPost(resizedFile.name, false, "image/jpeg");
  const { fields, url } = response as PresignedPost;
  const payload = new FormData();
  Object.keys(fields).forEach((key) =>
    payload.append(
      key,
      fields[key as keyof PresignedPost["fields"]] as string | Blob,
    ),
  );
  payload.set("file", resizedFile, resizedFile.name);
  await fetch(url, { body: payload, method: "POST" });
  // Return the S3 key for use in the profile
  return fields.key;
}

export async function uploadFile(
  file: File,
  options: {
    useAcceleration?: boolean;
    onProgress?: (progress: {
      totalParts: number;
      completedParts: number;
      totalBytes: number;
      uploadedBytes: number;
    }) => void;
    abortSignal?: AbortSignal;
  } = {},
  isRevisionFile = false,
): Promise<{ key: string; location: string }> {
  if (file == null) {
    throw new Error("No file provided for upload");
  }

  const cleanName = sanitizeFilename(file.name);

  // Always use multipart upload if supported (more reliable than direct upload)
  if (supportsMultipartUpload()) {
    try {
      return await uploadFileWithMultipart(
        file,
        {
          useAcceleration: options.useAcceleration ?? true,
          onProgress: options.onProgress,
          abortSignal: options.abortSignal,
        },
        isRevisionFile,
      );
    } catch (error) {
      console.error(
        "Multipart upload failed, falling back to direct upload:",
        error,
      );
      // Fall back to direct upload if multipart upload fails
    }
  }

  // Try direct upload with acceleration first
  try {
    const data = await presignedPost(
      cleanName,
      true,
      file.type,
      isRevisionFile,
    ).catch((error: unknown) => {
      console.error("Failed to get presigned URL:", error);
      const message = error instanceof Error ? error.message : "Unknown error";
      throw new Error(`Failed to initialize upload: ${message}`);
    });
    if (!data) {
      throw new Error(
        "Failed to initialize upload - no presigned URL received",
      );
    }

    const payload = new FormData();
    Object.keys(data.fields).forEach((key) =>
      payload.append(key, data.fields[key as keyof typeof data.fields]),
    );
    payload.set("file", file, cleanName);

    await uploadToS3({
      url: data.url,
      payload,
      abortSignal: options.abortSignal,
    });

    return {
      key: data.fields.key,
      location: `https://static.puntt.ai/${data.fields.key}`,
    };
  } catch (error) {
    // If upload with acceleration failed, try without acceleration as final fallback
    console.warn(
      "Direct upload with acceleration failed, trying without acceleration:",
      error,
    );
    posthogClient.capture("direct_upload_acceleration_failed_fallback", {
      fileName: cleanName,
      fileSize: file.size,
      error: error instanceof Error ? error.message : String(error),
    });

    const data = await presignedPost(
      cleanName,
      false,
      file.type,
      isRevisionFile,
    ).catch((error: unknown) => {
      console.error("Failed to get presigned URL without acceleration:", error);
      const message = error instanceof Error ? error.message : "Unknown error";
      throw new Error(
        `Failed to initialize upload without acceleration: ${message}`,
      );
    });
    if (!data) {
      throw new Error(
        "Failed to initialize upload - no presigned URL received",
      );
    }

    const payload = new FormData();
    Object.keys(data.fields).forEach((key) =>
      payload.append(key, data.fields[key as keyof typeof data.fields]),
    );
    payload.set("file", file, cleanName);

    await uploadToS3({
      url: data.url,
      payload,
      abortSignal: options.abortSignal,
    });

    posthogClient.capture("direct_upload_without_acceleration_success", {
      fileName: cleanName,
      fileSize: file.size,
    });

    return {
      key: data.fields.key,
      location: `https://static.puntt.ai/${data.fields.key}`,
    };
  }
}
